import { useAtomValue, useSetAtom } from 'jotai';
import { useRef, useState } from 'react';

import { ActionItemStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Divider,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import Upload16 from '@common/components/icons/Upload16';
import { formatTimestampToDate } from '@common/lib/time';

import { useGetSubmitSignedTarPaperwork } from '../../../api/progress/useGetSubmitSignedTarPaperwork';
import { useSubmitSignedTarPaperwork } from '../../../api/progress/useSubmitSignedTarPaperwork';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

import { fileToBase64 } from './utils/fileToBase64';

interface SubmitSignedTarPaperworkDetailProps {
	actionItem: ActionItem;
}

/**
 * SubmitSignedTarPaperworkDetail component for uploading final signed TAR paperwork scan.
 * Follows the same upload/replace UI pattern as ScanAndUploadFormsDetail.
 */
export const SubmitSignedTarPaperworkDetail = ({
	actionItem,
}: SubmitSignedTarPaperworkDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const submitTarPaperworkMutation = useSubmitSignedTarPaperwork(orderId);
	const { data: existingData, isLoading: isLoadingData } =
		useGetSubmitSignedTarPaperwork(orderId);

	// Track selected file
	const [selectedFile, setSelectedFile] = useState<File | null>(null);

	const isFormDisabled =
		submitTarPaperworkMutation.isPending || actionItem.status === ActionItemStatus.COMPLETE;

	const handleFileSelect = (file: File) => {
		setSelectedFile(file);
	};

	const handleReplaceFile = () => {
		setSelectedFile(null);
	};

	const handleSubmit = async () => {
		if (!selectedFile) return;

		try {
			const base64Data = await fileToBase64(selectedFile);

			await submitTarPaperworkMutation.mutateAsync({
				finalSignedTarPaperworkScan: {
					base64Data,
					type: selectedFile.type || 'application/pdf',
					name: selectedFile.name,
				},
			});

			toast({
				message: 'Final signed TAR paperwork uploaded successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to upload signed TAR paperwork';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	};

	// If already complete, show completed state
	if (actionItem.status === ActionItemStatus.COMPLETE) {
		return (
			<Stack itemsSpacing='24'>
				<Stack itemsSpacing='8'>
					<Text size='bodySmall' color='success'>
						✓ Final signed TAR paperwork has been uploaded
					</Text>
				</Stack>
				{existingData?.dateSellerSubmitted && (
					<Text size='bodySmall' color='secondary'>
						Uploaded on{' '}
						{formatTimestampToDate(new Date(existingData.dateSellerSubmitted).getTime()) ?? '—'}
					</Text>
				)}
			</Stack>
		);
	}

	return (
		<Stack itemsSpacing='24'>
			{/* Header */}
			<Stack itemsSpacing='8'>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Upload a scan of the complete final signed TAR paperwork package for record keeping.'}
				</Text>
			</Stack>

			{isLoadingData ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='60%' height={20} />
					<SkeletonRect width='80%' height={20} />
				</Stack>
			) : (
				<>
					{/* Upload section */}
					<Stack itemsSpacing='16'>
						<Stack itemsSpacing='4'>
							<Text size='body' weight='semibold'>
								Upload scan of the following document
							</Text>
							<Text size='footnote' color='secondary'>
								The document must be uploaded to complete this action item.
							</Text>
						</Stack>

						<Stack itemsSpacing='16'>
							<input
								ref={fileInputRef}
								type='file'
								accept='.pdf'
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) {
										handleFileSelect(file);
									}
								}}
								style={{ display: 'none' }}
								disabled={isFormDisabled}
							/>
							<Group
								itemsSpacing='8'
								itemsAlignY='center'
								itemsAlignX='space-between'>
								<Stack itemsSpacing='2'>
									<Text size='bodySmall'>Final Signed TAR Paperwork (PDF)</Text>
									{selectedFile && (
										<Text size='footnote' color='secondary'>
											{selectedFile.name}
										</Text>
									)}
								</Stack>

								{existingData?.isUploaded ? (
									<Text size='bodySmall' color='success'>
										✓ Uploaded
									</Text>
								) : selectedFile ? (
									<Group itemsSpacing='8' itemsAlignY='center'>
										<Text size='bodySmall' color='secondary'>
											Uploaded
										</Text>
										<Button
											variant='ghost'
											size='small'
											onPress={handleReplaceFile}
											isDisabled={isFormDisabled}
											aria-label='Replace Final Signed TAR Paperwork'>
											Replace
										</Button>
									</Group>
								) : (
									<Button
										variant='secondary'
										size='medium'
										onPress={() => {
											fileInputRef.current?.click();
										}}
										isDisabled={isFormDisabled}
										aria-label='Upload Final Signed TAR Paperwork'>
										<Group itemsSpacing='4' itemsAlignY='center'>
											<Upload16 />
											<Text size='bodySmall'>Upload</Text>
										</Group>
									</Button>
								)}
							</Group>
							<Divider />
						</Stack>
					</Stack>

					{/* Action buttons */}
					<Group itemsSpacing='8' noWrap>
						<Group.Item grow={1}>
							<Button
								variant='secondary'
								size='large'
								isFullWidth
								onPress={navigateBack}
								isDisabled={submitTarPaperworkMutation.isPending}>
								Cancel
							</Button>
						</Group.Item>
						<Group.Item grow={1}>
							<Button
								size='large'
								isFullWidth
								onPress={handleSubmit}
								isLoading={submitTarPaperworkMutation.isPending}
								isDisabled={!selectedFile || isLoadingData || isFormDisabled}>
								{'Confirm forms have been submitted'}
							</Button>
						</Group.Item>
					</Group>
				</>
			)}
		</Stack>
	);
};
